import React, { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import toast from "react-hot-toast";
import Organisation from "@/model/interface/Organisation";
import { OrgAddOnPriceEntry } from "@/model/interface/AddOn";
import {
  useGetOrgAddOnPricesQuery,
  useSetOrgAddOnPriceMutation,
  useClearOrgAddOnPriceMutation,
} from "@/store/admin_api";

function formatDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function OrgAddOnOverridesSection({ org }: { org: Organisation }) {
  const { data, isLoading } = useGetOrgAddOnPricesQuery(org.id ?? "", { skip: !org.id });
  const [setOrgAddOnPrice, { isLoading: isSaving }] = useSetOrgAddOnPriceMutation();
  const [clearOrgAddOnPrice] = useClearOrgAddOnPriceMutation();

  const [editing, setEditing] = useState<OrgAddOnPriceEntry | null>(null);
  const [draftDollars, setDraftDollars] = useState("");

  const openEdit = (entry: OrgAddOnPriceEntry) => {
    setEditing(entry);
    setDraftDollars((entry.listMonthlyCostCents / 100).toFixed(2));
  };

  const handleSave = async () => {
    if (!editing) {
      return;
    }
    const dollars = parseFloat(draftDollars);
    if (Number.isNaN(dollars) || dollars < 0) {
      toast.error("Enter a valid monthly price");
      return;
    }
    try {
      await setOrgAddOnPrice({
        orgId: org.id!,
        addOnType: editing.addOnType,
        monthlyCostCents: Math.round(dollars * 100),
      }).unwrap();
      toast.success(`Override set for ${editing.displayName}`);
      setEditing(null);
    } catch {
      toast.error("Failed to set override");
    }
  };

  const handleReset = async (entry: OrgAddOnPriceEntry) => {
    try {
      await clearOrgAddOnPrice({ orgId: org.id!, addOnType: entry.addOnType }).unwrap();
      toast.success(`Reverted ${entry.displayName} to default`);
    } catch {
      toast.error("Failed to clear override");
    }
  };

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-medium">Add-ons</h2>
      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Add-on</TableHead>
                <TableHead className="text-right">Default</TableHead>
                <TableHead className="text-right">Effective</TableHead>
                <TableHead className="w-40" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.map((entry) => (
                <TableRow key={entry.addOnType}>
                  <TableCell className="font-medium">
                    {entry.displayName}
                    {entry.overridden && (
                      <Badge variant="secondary" className="ml-2">
                        Custom
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {formatDollars(entry.defaultMonthlyCostCents)}
                  </TableCell>
                  <TableCell className="text-right">
                    {entry.effectiveMonthlyCostCents < entry.listMonthlyCostCents ? (
                      <>
                        <span className="mr-2 text-muted-foreground line-through">
                          {formatDollars(entry.listMonthlyCostCents)}
                        </span>
                        {formatDollars(entry.effectiveMonthlyCostCents)}
                        <span className="ml-1 text-xs text-muted-foreground">at cost</span>
                      </>
                    ) : (
                      formatDollars(entry.effectiveMonthlyCostCents)
                    )}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(entry)}>
                      Override
                    </Button>
                    {entry.overridden && (
                      <Button variant="ghost" size="sm" onClick={() => handleReset(entry)}>
                        Reset
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Override price</DialogTitle>
            <DialogDescription>
              {editing?.displayName} for {org.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="org-addon-price">Monthly price ($)</Label>
            <Input
              id="org-addon-price"
              type="number"
              min={0}
              step="0.01"
              value={draftDollars}
              onChange={(e) => setDraftDollars(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save override"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
